import { Socket } from 'socket.io-client';

interface RoomSubscription {
  roomId: string;
  subscribed: boolean;
  subscribedAt: Date;
}

export class SocketRoomManager {
  private subscriptions = new Map<string, RoomSubscription>();
  private socket: Socket | null = null;
  private storageKey = 'socket_room_subscriptions';

  constructor() {
    this.loadSubscriptions();
  }

  setSocket(socket: Socket) {
    console.log('🔌 SocketRoomManager: Setting socket instance');
    this.socket = socket;
    this.attachRoomEventListeners();

    // Auto-resubscribe to stored rooms when socket connects
    if (socket.connected) {
      console.log('✅ Socket already connected, resubscribing to rooms');
      this.resubscribeToActiveRooms();
    } else {
      console.log('⏳ Socket not yet connected, will resubscribe on connect');
    }
  }

  private attachRoomEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('🔄 SocketRoomManager: Socket connected, resubscribing to rooms');
      this.resubscribeToActiveRooms();
    });

    this.socket.on('room:subscribed', (data: any) => {
      const roomId = data?.roomId || data;
      if (roomId && this.subscriptions.has(roomId)) {
        const subscription = this.subscriptions.get(roomId)!;
        subscription.subscribed = true;
        subscription.subscribedAt = new Date();
        this.saveSubscriptions();
        console.log('✅ Room subscription confirmed:', roomId);
      }
    });

    this.socket.on('room:unsubscribed', (data: any) => {
      const roomId = data?.roomId || data;
      if (roomId && this.subscriptions.has(roomId)) {
        this.subscriptions.delete(roomId);
        this.saveSubscriptions();
        console.log('👋 Room unsubscription confirmed:', roomId);
      }
    });

    this.socket.on('disconnect', () => {
      // Mark all subscriptions as unsubscribed but keep them for reconnection
      this.subscriptions.forEach(subscription => {
        subscription.subscribed = false;
      });
    });
  }

  subscribeToRoom(roomId: string): void {
    if (!this.socket) {
      console.error('❌ Cannot subscribe to room: Socket not available');
      return;
    }

    // Check if already subscribed
    const existing = this.subscriptions.get(roomId);
    if (existing?.subscribed) {
      console.log('ℹ️ Already subscribed to room:', roomId);
      return;
    }

    // Add or update subscription
    this.subscriptions.set(roomId, {
      roomId,
      subscribed: false,
      subscribedAt: new Date()
    });

    // Emit subscription events
    if (this.socket.connected) {
      console.log('🚪 Subscribing to room:', roomId);
      this.socket.emit('subscribe:room', roomId);
      this.socket.emit('user:presence', {
        roomId,
        userType: 'merchant'
      });
    } else {
      console.log('⏳ Socket not connected, subscription will be sent when connected');
    }

    this.saveSubscriptions();
  }

  unsubscribeFromRoom(roomId: string): void {
    if (!this.socket) {
      console.warn('⚠️ Cannot unsubscribe from room: Socket not available');
      return;
    }

    const subscription = this.subscriptions.get(roomId);
    if (!subscription) {
      console.log('ℹ️ Not subscribed to room:', roomId);
      return;
    }

    if (this.socket.connected) {
      console.log('🚪 Unsubscribing from room:', roomId);
      this.socket.emit('unsubscribe:room', roomId);
      this.socket.emit('user:leave', { roomId });
    }

    this.subscriptions.delete(roomId);
    this.saveSubscriptions();
  }

  private resubscribeToActiveRooms(): void {
    if (!this.socket?.connected) {
      console.log('⏸️ Socket not connected, skipping resubscription');
      return;
    }

    const roomsToResubscribe = Array.from(this.subscriptions.keys());

    if (roomsToResubscribe.length === 0) {
      console.log('📭 No rooms to resubscribe to');
      return;
    }

    console.log('🔄 Resubscribing to rooms:', roomsToResubscribe);

    // Add a small delay to ensure socket is fully ready
    setTimeout(() => {
      if (!this.socket?.connected) {
        console.log('❌ Socket disconnected during resubscription');
        return;
      }

      roomsToResubscribe.forEach(roomId => {
        const subscription = this.subscriptions.get(roomId);
        if (subscription) {
          subscription.subscribed = false;
          console.log('🚪 Re-subscribing to room:', roomId);
          this.socket!.emit('subscribe:room', roomId);
          this.socket!.emit('user:presence', {
            roomId,
            userType: 'merchant'
          });
        }
      });
    }, 100); // Small delay to ensure socket is ready
  }

  getActiveSubscriptions(): string[] {
    return Array.from(this.subscriptions.keys());
  }

  isSubscribedToRoom(roomId: string): boolean {
    return this.subscriptions.get(roomId)?.subscribed || false;
  }

  clearAllSubscriptions(): void {
    const roomIds = Array.from(this.subscriptions.keys());
    roomIds.forEach(roomId => this.unsubscribeFromRoom(roomId));
  }

  private saveSubscriptions(): void {
    if (typeof window === 'undefined') return; // Skip on server-side

    try {
      const data = Array.from(this.subscriptions.entries()).map(([roomId, subscription]) => ({
        roomId,
        subscribedAt: subscription.subscribedAt.toISOString()
      }));
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save subscriptions:', error);
    }
  }

  private loadSubscriptions(): void {
    if (typeof window === 'undefined') return; // Skip on server-side

    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        data.forEach(({ roomId, subscribedAt }: any) => {
          this.subscriptions.set(roomId, {
            roomId,
            subscribed: false, // Will be reconfirmed on reconnect
            subscribedAt: new Date(subscribedAt)
          });
        });
        console.log('📁 Loaded subscriptions for rooms:', Array.from(this.subscriptions.keys()));
      }
    } catch (error) {
      console.error('Failed to load subscriptions:', error);
    }
  }
}

// Singleton instance
export const socketRoomManager = new SocketRoomManager();